import * as THREE from "three";

/** One tower the fader can act on: its group, and the world-space box it occupies at rest. */
export interface OccluderView { root: THREE.Object3D; box: THREE.Box3 }

/**
 * Towers stand between the camera and the road, so a tower placed on the near side hides the enemies walking behind
 * it. This fades any tower that covers a live target on screen while being closer to the camera than that target —
 * the tower stays visible as a ghost, and the fight behind it stays readable. Presentation only.
 *
 * The test is screen-space: project the tower's box corners to NDC for a rectangle, project each target to a point,
 * and compare depth. With a dozen towers and a few dozen enemies that is a few hundred cheap checks a frame, and it
 * needs no raycasting against geometry.
 */
export class TowerOcclusion {
  /** Opacity a covering tower fades to, and how fast it moves (per second). */
  static readonly FadedAlpha = 0.32;
  static readonly Speed = 5;
  /** Screen-space padding around the tower rectangle, in NDC, so units are ghosted just before they slip behind. */
  static readonly Pad = 0.02;
  private readonly alpha = new Map<THREE.Object3D, number>();
  private readonly owned = new WeakMap<THREE.Object3D, THREE.Material[]>();
  private static readonly v = new THREE.Vector3();

  /** `targets` are world positions that must stay visible (enemies, troops). */
  update(dt: number, camera: THREE.Camera, towers: readonly OccluderView[], targets: readonly THREE.Vector3[]): void {
    camera.updateMatrixWorld();
    // Runs every frame against every live unit, so it works in reused scratch space rather than allocating.
    const camPos = camera.getWorldPosition(this.camPos);
    const n = targets.length;
    if (this.screen.length < n * 3) this.screen = new Float64Array(Math.max(n * 3, this.screen.length * 2));
    const screen = this.screen;
    for (let i = 0; i < n; i++) {
      const t = targets[i];
      const p = TowerOcclusion.v.copy(t).project(camera);
      screen[i * 3] = p.x; screen[i * 3 + 1] = p.y; screen[i * 3 + 2] = camPos.distanceToSquared(t);
    }
    const r = this.rect;
    for (const t of towers) {
      let covers = false;
      if (this.project(t.box, camera)) {
        const near = camPos.distanceToSquared(t.box.getCenter(TowerOcclusion.v));
        for (let i = 0; i < n; i++) {
          if (screen[i * 3 + 2] <= near) continue;          // behind the tower from here, so nothing is hidden
          const x = screen[i * 3], y = screen[i * 3 + 1];
          if (x >= r.x0 && x <= r.x1 && y >= r.y0 && y <= r.y1) { covers = true; break; }
        }
      }
      const want = covers ? TowerOcclusion.FadedAlpha : 1;
      const now = this.alpha.get(t.root) ?? 1;
      if (Math.abs(now - want) < 0.002) { if (now !== want) this.apply(t.root, want); continue; }
      const next = now + Math.sign(want - now) * Math.min(Math.abs(want - now), TowerOcclusion.Speed * dt);
      this.alpha.set(t.root, next);
      this.apply(t.root, next);
    }
  }
  /** Scratch: the camera position, each target's screen x, y and squared distance, and one tower's screen rectangle. */
  private readonly camPos = new THREE.Vector3();
  private screen = new Float64Array(0);
  private readonly rect = { x0: 0, x1: 0, y0: 0, y1: 0 };

  /** Writes a world box's padded screen rectangle into `rect`; false when it is behind the camera. */
  private project(box: THREE.Box3, camera: THREE.Camera): boolean {
    let x0 = Infinity, x1 = -Infinity, y0 = Infinity, y1 = -Infinity;
    for (let i = 0; i < 8; i++) {
      const p = TowerOcclusion.v.set(i & 1 ? box.max.x : box.min.x, i & 2 ? box.max.y : box.min.y, i & 4 ? box.max.z : box.min.z).project(camera);
      if (p.z > 1) return false;
      x0 = Math.min(x0, p.x); x1 = Math.max(x1, p.x); y0 = Math.min(y0, p.y); y1 = Math.max(y1, p.y);
    }
    const pad = TowerOcclusion.Pad, r = this.rect;
    r.x0 = x0 - pad; r.x1 = x1 + pad; r.y0 = y0 - pad; r.y1 = y1 + pad;
    return true;
  }

  /**
   * Materials are cached and shared between clones, so fading one tower would fade the lot: give this instance its
   * own copies the first time it needs to fade, then drive their opacity.
   */
  private apply(root: THREE.Object3D, alpha: number): void {
    this.alpha.set(root, alpha);
    let mats = this.owned.get(root);
    if (!mats) {
      if (alpha >= 0.999) return;                          // nothing to do until it actually fades
      mats = [];
      root.traverse((o) => {
        const mesh = o as THREE.Mesh;
        if (!mesh.isMesh || o.userData.shadowProxy === true) return;   // a proxy casts through its depth material only
        const list = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
        // keep each material's shader hooks (lit extras, a skin kit's slots and motion): Material.clone drops them
        const copies = list.map((m) => { const c = m.clone(); c.onBeforeCompile = m.onBeforeCompile; c.customProgramCacheKey = m.customProgramCacheKey; return c; });
        mesh.material = copies.length === 1 ? copies[0] : copies;
        mats!.push(...copies);
      });
      this.owned.set(root, mats);
    }
    const faded = alpha < 0.999;
    for (const m of mats) {
      m.transparent = faded;
      m.opacity = alpha;
      m.depthWrite = !faded;
      // three.js draws a transparent double-sided material twice (back faces, then front) and recompiles-checks its
      // program on both, every frame the tower stays faded. A ghost needs only its front faces.
      const side = faded ? THREE.FrontSide : this.side.get(m) ?? m.side;
      if (m.side !== side) { if (!this.side.has(m)) this.side.set(m, m.side); m.side = side; m.needsUpdate = true; }
    }
  }
  /** A faded copy's authored side, restored when it is solid again. */
  private readonly side = new WeakMap<THREE.Material, THREE.Side>();

  /** Drop a tower that has been removed or rebuilt, so its cloned materials are not reused. */
  forget(root: THREE.Object3D): void { this.alpha.delete(root); this.owned.delete(root); }
}
